// ---------------------------------------------------------------------------
// Experiment Configs — type-safe experiment definitions
// ---------------------------------------------------------------------------
//
// When adding a new experiment, define its config here. The `variations` tuple
// tells TypeScript exactly which variation strings are valid. The `useExperiment`
// hook uses this to narrow the returned `variation` field so you get
// autocomplete and compile-time errors if you check for a variation that
// doesn't exist.
//
// Common variation patterns:
//   - Standard A/B:    ['control', 'on'] DEFAULT
//   - Multi-variant:   ['control', 'variant_1', 'variant_2']
// ---------------------------------------------------------------------------

/**
 * A type-safe experiment configuration that links an experiment ID
 * to its known variation strings.
 */
export interface ExperimentConfig<
  TId extends string = string,
  TVars extends ReadonlyArray<string> = ReadonlyArray<string>,
> {
  /** The provider experiment ID. */
  readonly id: TId
  /** The exhaustive list of variation strings for this experiment. */
  readonly variations: TVars
  /** The experiment provider. Defaults to Optimizely for existing experiments. */
  readonly provider?: 'optimizely' | 'cherto'
}

export interface ChertoExperimentConfig<
  TId extends string = string,
  TVars extends ReadonlyArray<string> = ReadonlyArray<string>,
> extends ExperimentConfig<TId, TVars> {
  readonly provider: 'cherto'
  readonly keyStrategy: 'environment-qualified' | 'production-only'
}

/** Default variations for standard A/B experiments. */
const DEFAULT_VARIATIONS = ['control', 'on'] as const

/** Helper to create an ExperimentConfig with full type inference. */
function defineExperiment<
  TId extends string,
  const TVars extends ReadonlyArray<string>,
>(id: TId, variations: TVars, provider?: ExperimentConfig['provider']): ExperimentConfig<TId, TVars>
function defineExperiment<TId extends string>(id: TId, provider?: ExperimentConfig['provider']): ExperimentConfig<TId, typeof DEFAULT_VARIATIONS>
function defineExperiment<
  TId extends string,
  const TVars extends ReadonlyArray<string>,
>(id: TId, variationsOrProvider?: TVars | ExperimentConfig['provider'], provider?: ExperimentConfig['provider']) {
  const variations = Array.isArray(variationsOrProvider) ? variationsOrProvider : DEFAULT_VARIATIONS
  const experimentProvider = Array.isArray(variationsOrProvider) ? provider : variationsOrProvider

  return {
    id,
    variations,
    ...(experimentProvider ? { provider: experimentProvider } : {}),
  }
}

function defineChertoExperiment<
  TId extends string,
  const TVars extends ReadonlyArray<string>,
>(id: TId, variations: TVars): ChertoExperimentConfig<TId, TVars> {
  if (id.startsWith('prod-') || id.startsWith('staging-')) {
    throw new Error(`Cherto experiment "${id}" must use its logical key without an environment prefix`)
  }

  return {
    ...defineExperiment(id, variations, 'cherto'),
    provider: 'cherto',
    keyStrategy: 'environment-qualified',
  }
}

function defineProductionOnlyChertoExperiment<
  TId extends string,
  const TVars extends ReadonlyArray<string>,
>(id: TId, variations: TVars): ChertoExperimentConfig<TId, TVars> {
  return {
    ...defineExperiment(id, variations, 'cherto'),
    provider: 'cherto',
    keyStrategy: 'production-only',
  }
}

/**
 * Type-safe experiment configs.
 *
 * Pass these to `useExperiment()` instead of raw experiment IDs to get typed
 * `variation` in the result — preventing typos and making
 * exhaustive checks possible.
 *
 * Variations default to `['control', 'on']`. Only specify variations when the
 * experiment uses non-standard ones (e.g. multi-variant).
 *
 * Usage:
 * ```ts
 * // Standard A/B — variations default to ['control', 'on']
 * const { isAnyVariant } = useExperiment(Experiments.myExperiment)
 *
 * // Multi-variant — specify variations explicitly
 * const { variation } = useExperiment(Experiments.myMultiVariant)
 * // variation: 'control' | 'variant_a' | 'variant_b' | undefined
 * ```
 */
export const Experiments = {
  croRebrand: defineExperiment('cro_rebrand'),
  nhaForceDatesOnTileClick: defineExperiment('nha_force_dates_on_tile_click'),
  isSupportAssistantEnabled: defineExperiment('opex_support_assistant_customer_pool'),
  emailChangeEnabled: defineExperiment('opex_email_change'),
  securePhoneNumberChangeEnabled: defineExperiment('csx_secure_phone_number_change'),
  croLPFreePreviewAfterSignUpLogIn: defineExperiment(
    'cro_offer_lp_free_preview_after_sign_up_log_in',
    ['control', 'variant_1', 'variant_2'] as const,
  ),
  croAlwaysOnNewMobileBooking: defineExperiment('cro_lpc_new_mobile_booking'),
  paymentsDepositOptionForHotelsAbove1999V2: defineExperiment('payments_deposit_option_for_hotels_above_1999_v2'),
  carHireBundledDiscountUpcomingOrderHomepageCarousel: defineExperiment('car_hire_bundled_discount_upcoming_order_homepage_carousel'),
  croPopularOptionBookingRemaining: defineExperiment('cro_popular_option_booking_remaining'),
  growthShowOfferUrgencyCountdownLabel: defineExperiment('growth_force_show_offer_urgency_countdown_label'),
  croBundledFlexibleFlightFaresV2: defineExperiment('cro_bundled_flexible_flight_fares_v2'),
  croHideHomepageSearchEmergingMarkets: defineExperiment('cro_hide_homepage_search_emerging_markets'),
  croHomepageCarouselMarketingHeadlineV2: defineExperiment('cro_homepage_carousel_marketing_headline_v2', ['control', 'on'] as const),
  lereNonAUHomepageTopDealsHeroCarousel: defineExperiment('lere_nonau_homepage_topdeals_hero_carousel'),
  lerePersonalisedHomePageHeroCarousel: defineExperiment('lere_personalised_home_page_hero_carousel'),
  lerePersonalisedHomePageHeroCarouselV4: defineExperiment('lere_home_page_carousel_v4'),
  lerePersonalisedHomePageHeroCarouselV5: defineExperiment('lere_home_page_carousel_v5'),
  croPrefetchInflowCheckoutExperiences: defineExperiment('cro_prefetch_inflow_checkout_experiences'),
  flightsPrefetchFlightsFromOffer: defineExperiment('flights_prefetch_flights_from_offer'),
  flightsCombinedReturnLegs: defineExperiment('flights_combined_return_legs'),
  flightsBundledCombinedReturnLegs: defineExperiment('flights_bundled_combined_return_legs'),
  carHireObjectiveIntentMessage: defineExperiment('car_hire_objective_intent_message'),
  croFlexCfmrBedbankPackages: defineExperiment('cro_flex_cfmr_bedbank_packages'),
  growthMetasearchPerNightPricing: defineExperiment('growth_metasearch_per_night_pricing'),
  growthMetasearchMobileDesignV2: defineExperiment('growth_metasearch_mobile_design_v2'),
  croOrderDownloadPdf: defineExperiment('cro_order_download_pdf'),
  cruiseAutoFocusNextFilter: defineExperiment('cruise_auto_focus_next_filter'),
  cruiseMpvRanking: defineExperiment('cruise_mpv_ranking'),
  labnableVideoBackground: defineExperiment('lab_enable_video_background'),
  loyaltyCommSubBonus: defineExperiment(
    'loyalty_communication_resubscription_bonus',
    ['control', '500_points', '750_points'] as const,
  ),
  growthPaidOfferGalleryVariant: defineExperiment('growth_paid_offer_gallery_variant'),
  croExpressCheckoutAutoDefault: defineExperiment('cro_express_checkout_auto_default'),
  croCheckoutPaymentOptions: defineExperiment('cro_checkout_payment_options'),
  croCheckoutIncompleteInformationModal: defineExperiment('cro_checkout_incomplete_information_modal'),
  seoStaticHotelsV2Ab: defineExperiment('seo_static_hotels_v2_ab'),
  luxPlusReconsiderCheckoutBanner: defineExperiment('lux_plus_reconsider_checkout_banner'),
  searchNlsLlmRerank: defineExperiment('search_nls_llm_rerank'),
  searchMapGeographicDiversity: defineExperiment('search_map_geographic_diversity'),
  croDisplayLtleOnHomepage: defineExperiment('cro_display_ltle_on_homepage'),
  croTrustpilotReviewFeedInOfferPage: defineExperiment('cro_trustpilot_review_feed_in_offer_page'),
  croPostTripReviewHomepage: defineExperiment('cro_post_trip_review_homepage_v2'),
  croPopularFlightsHomePage: defineExperiment('cro_popular_flights_home_page'),
  experiencesCollections: defineExperiment('experiences_collections'),
  lereRankerClustering: defineExperiment('lere_ranker_clustering'),
  opexLESAEmptyChatFreeText: defineExperiment('opex_lesa_empty_chat_free_text_ab_test'),
  opexBookingAuthorityReferralBannerAbTest: defineExperiment(
    'opex_booking_authority_referral_banner_ab_test_v2',
    ['control', 'variant_with_referral_banner', 'referral_banner_variant_no_t_and_c'] as const,
  ),
  croChildrenStayFreeTag: defineExperiment('cro_children_stay_free_tag'),
  // Funnel optimiser A/B tests (see src/checkout/selectors/funnel.ts for usage)
  funnelOptimiserRuleId16: defineExperiment('funnel_optimiser_ruleid_16'),
  funnelOptimiserRuleId17: defineExperiment('funnel_optimiser_ruleid_17'),
  funnelOptimiserRuleId18: defineExperiment('funnel_optimiser_ruleid_18'),
  paymentsLpc50PercentDepositSchedule: defineExperiment('payments_lpc_50_percent_deposit_schedule'),
  lereBedbankSimilarOffer: defineExperiment('lere_bedbank_similar_offer'),
  lereSimilarToCarousel: defineExperiment('lere_similar_to_carousel', ['control', 'on'] as const),
  opexLesaV2Assistant: defineExperiment(
    'opex_lesa_v2_assistant_ab_test',
    ['V1', 'V2'] as const,
  ),
  opexLesaNlsOffersCarousel: defineExperiment('opex_lesa_nls_offers_carousel_ab_test'),
  lesaNoAnswerAgentHandoff: defineExperiment(
    'lesa_no_answer_agent_handoff_variation',
    ['off', 'agent_handoff'] as const,
  ),
  lesaBookingPickerComponent: defineExperiment(
    'lesa_booking_picker_component',
    ['control', 'order_list'] as const,
  ),
  cruiseOfferPageEarlyTravellerSelection: defineExperiment('cruise_offer_page_early_traveller_selection'),
  cruiseCheckoutTravellerDetailsPlacement: defineExperiment(
    'cruise_checkout_traveller_details_placement',
    ['control', 'variant'] as const,
  ),
  opexCheckoutOptionalGuestTravellerForms: defineExperiment('opex_checkout_optional_guest_traveller_forms'),
  opexCheckoutTravellerCardShareBooking: defineExperiment('opex_checkout_traveller_card_share_booking'),
  croCheckoutCancellationPolicyTimeline: defineExperiment('cro_checkout_cancellation_policy_timeline_2'),
  croCheckoutExperiencesUpsellRedesign: defineExperiment(
    'cro_checkout_experiences_upsell_redesign',
    ['control', 'variant'] as const,
  ),
  internationalCheckoutFlow: defineExperiment('international_checkout_flow'),
  croMobileWebAppLikeCheckout: defineExperiment('cro_mobile_web_app_like_checkout'),
  paymentsLpp50PercentDepositSchedule: defineExperiment('payments_lpp_50_percent_deposit_schedule'),
  croCheckoutProgressStepper: defineExperiment('cro_checkout_progress_stepper'),
  /**
   * Controls visibility of the points burn UI on search results pages (toggle + slider).
   * This does NOT affect points burn at checkout, which has its own separate enablement logic.
   */
  loyaltySocieteBurn: defineExperiment('loyalty_societe_burn'),
  searchSalesCtrPaidTrafficRanking: defineExperiment('search_sales_ctr_paid_traffic_ranking'),
  searchLowViewDestinationsSalesCtrRanking: defineExperiment('search_low_view_destinations_sales_ctr_ranking'),
  searchLowViewDestinationsLuxuryScoreRanking: defineExperiment('search_low_view_destinations_luxury_score_ranking'),
  searchNearCentreFlashBoostRanking: defineExperiment('search_near_centre_flash_boost'),
  searchLargeOccupancyAffordabilityRanking: defineExperiment('search_large_occupancy_affordability_ranking_v2'),
  searchDatedPriceCacheSort: defineExperiment('search_dated_price_cache_sort'),
  croOfferPageHighlightTagsV2: defineExperiment('cro_offer_page_highlight_tags_v2'),
  croMobileWebPackageSelectCta: defineExperiment('cro_mobile_web_package_select_cta'),
  croRoomAmenitiesInsteadOfDescription: defineExperiment('cro_room_amenities_instead_of_description'),
  croSppCollapsedPackages: defineExperiment(
    'cro_spp_collapsed_packages',
    ['control', 'collapsed', 'expanded'] as const,
  ),
  croSppCollapsedPackagesV2: defineExperiment(
    'cro_spp_collapsed_packages_v2',
    ['control', 'collapsed', 'expanded'] as const,
  ),
  croUrgencyOnSppRooms: defineExperiment('cro_urgency_on_spp_rooms'),
  nhaHomesAndVillasToggleInSearch: defineExperiment('nha_homes_and_villas_toggle_in_search'),
  searchTilePropertyNearby: defineExperiment('search_tile_property_nearby'),
  commsCompactMobileSearchTile: defineExperiment('comms_compact_mobile_search_tile'),
  tripAiTripPlannerOnFlightsPage: defineExperiment('trip_ai_trip_planner_on_flights_page'),
  tripChatMobile: defineExperiment('trip_chat_mobile'),
  croMobileWebOfferHeader: defineExperiment('cro_mobile_web_offer_header'),
  croPcmTestimonialOnOfferPage: defineExperiment('cro_pcm_testimonial_on_offer_page'),
  croSoldOutFlashPackagesState: defineExperiment('cro_sold_out_flash_packages_state'),
  croFixedFlashLengthOfStay: defineExperiment('cro_fixed_flash_length_of_stay'),
  croSocialProofRecentPurchase: defineExperiment('cro_social_proof_recent_purchase_v2'),
  croSocialProofSpp: defineExperiment('cro_social_proof_spp'),
  croSocialProofTours: defineExperiment('cro_social_proof_tours'),
  lereEndingSoonRanker: defineExperiment('lere_ending_soon_ranker'),
  croOneClickBookingRestoration: defineExperiment('cro_one_click_booking_restoration'),
  croRelativePricingComparison: defineExperiment('cro_relative_pricing_comparison', ['control', 'variant_a', 'variant_b'] as const),
  croFlashPackageTickbox: defineExperiment('cro_flash_package_tickbox'),
  standaloneFlightUpsellExperiences: defineExperiment('standalone_flight_upsell_experiences'),
  tripHotelOfferSurface: defineExperiment('trip-hotel-offer-surface'),
  searchHiddenPreviewBannerSearchResult: defineExperiment('search_hidden_preview_banner_search_result'),
  /**
   * Hotel search filter sections v2 A/B — treatment adds facilities /
   * room amenities / bed type sections, hides amenities, and radio bedrooms.
   * Also drives svc-search rollout `hotel_filter_sections_v2`.
   */
  searchHotelFilterSectionsV2: defineExperiment('search_hotel_filter_sections_v2'),
  sponsoredSearchTile: defineExperiment('sponsored_search_tile'),
  croHomepageHeroOffersSsrTimeout: defineExperiment('cro_homepage_hero_offers_ssr_timeout'),
  growthOfferRegionBypass: defineExperiment('offer_region_bypass'),
} as const

export const ChertoExperiments = {
  croMobileWebRoomUpgradeModal: defineProductionOnlyChertoExperiment(
    'cro-mobile-web-room-upgrade-modal',
    ['control', 'on'] as const,
  ),
  // Existing live experiments must retain their literal Cherto keys.
  croHideNearbyGooglePlaces: defineProductionOnlyChertoExperiment(
    'cro-hide-nearby-google-places-on-offer-page',
    ['control', 'variant'] as const,
  ),
  croInsurancePriceIncrease: defineChertoExperiment('cro-insurance-price-increase', ['control', 'variant'] as const),
  // The v2 Cherto record is live with an unprefixed key.
  croCancellationTimelineOnOfferPageV2: defineProductionOnlyChertoExperiment(
    'show-cancellation-policy-as-a-timeline-v2',
    ['control', 'variant'] as const,
  ),
  croFacilitiesReworkV2: defineProductionOnlyChertoExperiment('cro-facilities-rework-v2', ['control', 'variant'] as const),
} as const satisfies Record<string, ChertoExperimentConfig<string>>

export type Experiment =
  | typeof Experiments[keyof typeof Experiments]
  | typeof ChertoExperiments[keyof typeof ChertoExperiments]

export type ExperimentId = Experiment['id']
